'use client';

import { c as _c } from "react/compiler-runtime";
import type { ChainId } from '@morpho-org/blue-sdk';
import type { Time } from '@morpho-org/morpho-ts';
import type { Address } from 'viem';
import { InfoTooltip, Select, type VariationDisplayMode, VariationLabel } from '@morpho-org/ui-v6/client';
import { Card } from '@morpho-org/ui-v6/server';
import { formatters } from '@morpho-org/ui-v6/utils';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import SimpleChart from '@/components/charts/components/SimpleChart';
import { formatTimeseriesData } from '@/components/charts/helpers/highcharts';
import { useHighchartsOptions } from '@/components/charts/hooks/useHighchartsOptions';
import { defaultTimeFrame, timeFrameOptions } from '@/config/table';
import { splitValueAndUnit } from '@/helpers/number';
import { sharePriceQueryOptions, toHistoryRange } from './sharePrice.query';
import { toVariationValues } from './variation';
export interface ChartSharePriceClientProps {
  chainId: ChainId;
  address: Address;
}
interface Header {
  symbol: string | null;
  sharePrice: number | null;
  updatedAt: number;
}

/**
 * The vault's share price history. The server hydrates the default timeframe; another one shows
 * the chart's loading state until it arrives, and a failed range shows the chart's error with the
 * header and the selector still usable.
 */
const ChartSharePriceClient = t0 => {
  const $ = _c(64);
  const {
    chainId,
    address
  } = t0;
  const {
    palette
  } = useHighchartsOptions();
  const [timeFrame, setTimeFrame] = useState(defaultTimeFrame);
  let t1;
  if ($[0] !== address || $[1] !== chainId || $[2] !== timeFrame) {
    t1 = sharePriceQueryOptions({
      chainId,
      address,
      range: toHistoryRange(timeFrame)
    });
    $[0] = address;
    $[1] = chainId;
    $[2] = timeFrame;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== t1) {
    t2 = {
      ...t1,
      placeholderData: keepPreviousData
    };
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const {
    data,
    dataUpdatedAt,
    isError,
    isPlaceholderData,
    isPending
  } = useQuery(t2);
  const loading = isPending || isPlaceholderData && !isError;
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      symbol: null,
      sharePrice: null,
      updatedAt: 0
    };
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const [header, setHeader] = useState(t3);
  if (data !== undefined && !isPlaceholderData && dataUpdatedAt > header.updatedAt) {
    setHeader({
      symbol: data.asset.symbol,
      sharePrice: data.sharePrice,
      updatedAt: dataUpdatedAt
    });
  }
  const {
    symbol,
    sharePrice: currentSharePrice
  } = header;
  let formattedData;
  let formattedValue;
  let t4;
  if ($[7] !== currentSharePrice || $[8] !== data?.points || $[9] !== isError) {
    formattedData = formatTimeseriesData(isError ? [] : (data?.points ?? []).map(_temp));
    let t5;
    if ($[13] !== currentSharePrice) {
      t5 = splitValueAndUnit(formatters.short.default("-").digits(6).of(currentSharePrice));
      $[13] = currentSharePrice;
      $[14] = t5;
    } else {
      t5 = $[14];
    }
    const split = t5;
    const t6 = split.value ?? null;
    const t7 = split.unit ?? "";
    let t8;
    if ($[15] !== t6 || $[16] !== t7) {
      t8 = {
        value: t6,
        unit: t7
      };
      $[15] = t6;
      $[16] = t7;
      $[17] = t8;
    } else {
      t8 = $[17];
    }
    formattedValue = t8;
    t4 = toVariationValues(formattedData);
    $[7] = currentSharePrice;
    $[8] = data?.points;
    $[9] = isError;
    $[10] = formattedData;
    $[11] = formattedValue;
    $[12] = t4;
  } else {
    formattedData = $[10];
    formattedValue = $[11];
    t4 = $[12];
  }
  const variationValues = t4;
  const [variationDisplayMode, setVariationDisplayMode] = useState("percentage");
  let t5;
  if ($[18] !== formattedData || $[19] !== palette.areaChartColors[0] || $[20] !== palette.colors[0]) {
    t5 = [{
      type: "areaspline" as const,
      name: "Position Unit Price",
      data: formattedData,
      color: palette.colors[0],
      lineColor: palette.colors[0],
      fillColor: palette.areaChartColors[0]
    }];
    $[18] = formattedData;
    $[19] = palette.areaChartColors[0];
    $[20] = palette.colors[0];
    $[21] = t5;
  } else {
    t5 = $[21];
  }
  const series = t5;
  const t6 = data?.partial ? "true" : undefined;
  let t7;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      lineHeight: "normal"
    };
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  const t8 = symbol === null ? "" : ` (${symbol})`;
  let t9;
  if ($[23] !== t8) {
    t9 = <span className="text-secondary-foreground text-xs" style={t7}>Position Unit Price{t8}</span>;
    $[23] = t8;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <InfoTooltip tooltipContent="This chart tracks the price of one vault position unit as an exchange rate against the deposited asset. Line growth represents accrued yield, while a sudden drop signals a realized loss or bad debt." />;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== t9) {
    t11 = <div className="gap-xs flex items-center">{t9}{t10}</div>;
    $[26] = t9;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] !== formattedValue.value) {
    t12 = <span className="text-title-sm">{formattedValue.value}</span>;
    $[28] = formattedValue.value;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] !== formattedValue.unit) {
    t13 = <span className="text-title-sm text-muted-foreground">{formattedValue.unit}</span>;
    $[30] = formattedValue.unit;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  let t14;
  if ($[32] !== t12 || $[33] !== t13) {
    t14 = <div className="flex w-[fit-content] cursor-default items-center gap-[2px]" data-testid="vault-v1-share-price-value">{t12}{t13}</div>;
    $[32] = t12;
    $[33] = t13;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  const t15 = symbol ?? undefined;
  let t16;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = () => setVariationDisplayMode(_temp2);
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  let t17;
  if ($[36] !== loading || $[37] !== t15 || $[38] !== variationDisplayMode || $[39] !== variationValues) {
    t17 = <VariationLabel values={variationValues} unit={t15} digits={6} displayMode={variationDisplayMode} onClick={t16} textClassName="text-xs" isLoading={loading} />;
    $[36] = loading;
    $[37] = t15;
    $[38] = variationDisplayMode;
    $[39] = variationValues;
    $[40] = t17;
  } else {
    t17 = $[40];
  }
  let t18;
  if ($[41] !== t14 || $[42] !== t17) {
    t18 = <div className="gap-sm flex items-center">{t14}{t17}</div>;
    $[41] = t14;
    $[42] = t17;
    $[43] = t18;
  } else {
    t18 = $[43];
  }
  let t19;
  if ($[44] !== t11 || $[45] !== t18) {
    t19 = <div className="gap-xs flex flex-col">{t11}{t18}</div>;
    $[44] = t11;
    $[45] = t18;
    $[46] = t19;
  } else {
    t19 = $[46];
  }
  let t20;
  if ($[47] !== timeFrame) {
    t20 = <div className="gap-sm flex flex-wrap items-center"><Select options={timeFrameOptions} value={timeFrame} onChange={setTimeFrame} /></div>;
    $[47] = timeFrame;
    $[48] = t20;
  } else {
    t20 = $[48];
  }
  let t21;
  if ($[49] !== t19 || $[50] !== t20) {
    t21 = <div className="gap-sm flex flex-wrap items-center justify-between">{t19}{t20}</div>;
    $[49] = t19;
    $[50] = t20;
    $[51] = t21;
  } else {
    t21 = $[51];
  }
  let t22;
  if ($[52] !== isError || $[53] !== loading) {
    t22 = {
      loading,
      error: isError
    };
    $[52] = isError;
    $[53] = loading;
    $[54] = t22;
  } else {
    t22 = $[54];
  }
  const t23 = symbol ?? "";
  let t24;
  if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = {
      chart: {
        plotBackgroundColor: "var(--muted)"
      }
    };
    $[55] = t24;
  } else {
    t24 = $[55];
  }
  let t25;
  if ($[56] !== series || $[57] !== t22 || $[58] !== t23) {
    t25 = <SimpleChart statuses={t22} unit={t23} height="224px" series={series} digits={6} additionalOptions={t24} />;
    $[56] = series;
    $[57] = t22;
    $[58] = t23;
    $[59] = t25;
  } else {
    t25 = $[59];
  }
  let t26;
  if ($[60] !== t21 || $[61] !== t25 || $[62] !== t6) {
    t26 = <Card className="gap-lg py-lg px-md md:px-lg w-[100%] flex-col" withBorder={true} data-testid="vault-v1-share-price-chart" data-partial={t6}>{t21}{t25}</Card>;
    $[60] = t21;
    $[61] = t25;
    $[62] = t6;
    $[63] = t26;
  } else {
    t26 = $[63];
  }
  return t26;
};
export default ChartSharePriceClient;
function _temp(t0) {
  const {
    timestamp,
    sharePrice
  } = t0;
  return {
    x: timestamp,
    y: sharePrice
  };
}
function _temp2(prev) {
  return prev === "percentage" ? "amount" : "percentage";
}